import { useEffect, useRef, type RefObject } from 'react';

/** Load the next page once scroll depth reaches this fraction (0.9 = near bottom). */
export const ASSET_LOGS_TABLE_LOAD_MORE_SCROLL_RATIO = 0.9;

/** @deprecated Use {@link ASSET_LOGS_TABLE_LOAD_MORE_SCROLL_RATIO}. */
export const ASSET_LOGS_TABLE_LOAD_MORE_THRESHOLD_PX = 200;

type Options = {
  enabled: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  /** When true, stop auto-fetch so the user can retry manually. */
  isFetchNextPageError?: boolean;
  onLoadMore: () => void | Promise<void>;
  tableWrapRef: RefObject<HTMLElement | null>;
  /** Changes when merged log rows grow — triggers post-fetch scroll re-check. */
  loadedItemCount?: number;
  /** @default ASSET_LOGS_TABLE_LOAD_MORE_SCROLL_RATIO */
  loadMoreScrollRatio?: number;
};

export function assetLogsTableDistanceFromBottom(element: HTMLElement): number {
  return element.scrollHeight - element.scrollTop - element.clientHeight;
}

export function shouldLoadMoreAssetLogsTable(
  element: HTMLElement,
  minScrollDepthRatio: number = ASSET_LOGS_TABLE_LOAD_MORE_SCROLL_RATIO,
): boolean {
  const maxScroll = element.scrollHeight - element.clientHeight;
  if (maxScroll <= 0) {
    return false;
  }
  return scrollDepthRatio(element) >= minScrollDepthRatio;
}

export function scrollDepthRatio(element: HTMLElement): number {
  const maxScroll = element.scrollHeight - element.clientHeight;
  if (maxScroll <= 0) {
    return 0;
  }
  return element.scrollTop / maxScroll;
}

/** @deprecated Use resolveAssetLogsTableScrollElement */
export function resolveAssetLogsTableBody(
  anchor: HTMLElement | null,
): HTMLElement | null {
  const wrap = anchor?.closest('.evimo-table-wrap');
  return resolveAssetLogsTableScrollElement(
    wrap instanceof HTMLElement ? wrap : null,
  );
}

/** Table tbody when it scrolls; otherwise the landing page scroller (single vertical bar). */
export function resolveAssetLogsInfiniteScrollTargets(
  wrap: HTMLElement | null,
): HTMLElement[] {
  const tableScrollers = collectAssetLogsTableScrollElements(wrap);
  if (tableScrollers.length > 0) {
    return tableScrollers;
  }
  const landing = resolveLandingContentScroll(wrap);
  return landing ? [landing] : [];
}

export function resolveAssetLogsTableScrollElement(
  wrap: HTMLElement | null,
): HTMLElement | null {
  return resolveAssetLogsInfiniteScrollTargets(wrap)[0] ?? null;
}

/** Every element that can vertically scroll within an Evimo table wrap. */
export function collectAssetLogsTableScrollElements(
  wrap: HTMLElement | null,
): HTMLElement[] {
  if (!wrap) {
    return [];
  }

  const seen = new Set<HTMLElement>();
  const ordered: HTMLElement[] = [];

  const pushIfScrollable = (node: HTMLElement | null) => {
    if (!node || seen.has(node)) {
      return;
    }
    if (node.scrollHeight <= node.clientHeight + 2) {
      return;
    }
    seen.add(node);
    ordered.push(node);
  };

  const body = wrap.querySelector('.ant-table-body');
  if (body instanceof HTMLElement) {
    pushIfScrollable(body);
    return ordered;
  }

  const content = wrap.querySelector('.ant-table-content');
  if (content instanceof HTMLElement) {
    pushIfScrollable(content);
    return ordered;
  }

  pushIfScrollable(wrap);
  return ordered;
}

export function resolveLandingContentScroll(
  anchor: HTMLElement | null,
): HTMLElement | null {
  const landingScroll =
    anchor?.closest('.landing-content-scroll') ??
    document.querySelector('.landing-content-scroll');

  return landingScroll instanceof HTMLElement ? landingScroll : null;
}

function scheduleAfterLayout(callback: () => void): () => void {
  let innerRaf = 0;
  const outerRaf = requestAnimationFrame(() => {
    innerRaf = requestAnimationFrame(callback);
  });
  return () => {
    cancelAnimationFrame(outerRaf);
    cancelAnimationFrame(innerRaf);
  };
}

/**
 * Infinite scroll for asset logs: load when scroll passes the configured depth (default: 0.9).
 * Idle first paint does not fetch; any scroll/wheel in the table enables loading.
 */
export function useLogsInfiniteScroll({
  enabled,
  hasNextPage,
  isFetchingNextPage,
  isFetchNextPageError = false,
  onLoadMore,
  tableWrapRef,
  loadedItemCount = 0,
  loadMoreScrollRatio = ASSET_LOGS_TABLE_LOAD_MORE_SCROLL_RATIO,
}: Options) {
  const onLoadMoreRef = useRef(onLoadMore);
  const isFetchingNextPageRef = useRef(isFetchingNextPage);
  const isFetchNextPageErrorRef = useRef(isFetchNextPageError);
  const hasNextPageRef = useRef(hasNextPage);
  const enabledRef = useRef(enabled);
  const loadMoreScrollRatioRef = useRef(loadMoreScrollRatio);
  const userHasScrolledTableRef = useRef(false);
  const loadMoreQueuedRef = useRef(false);

  onLoadMoreRef.current = onLoadMore;
  isFetchingNextPageRef.current = isFetchingNextPage;
  isFetchNextPageErrorRef.current = isFetchNextPageError;
  hasNextPageRef.current = hasNextPage;
  enabledRef.current = enabled;
  loadMoreScrollRatioRef.current = loadMoreScrollRatio;

  const scrollElementsRef = useRef<HTMLElement[]>([]);
  const evaluateScrollRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!enabled) {
      userHasScrolledTableRef.current = false;
      loadMoreQueuedRef.current = false;
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let bindAttempts = 0;
    let rafId = 0;
    let timeoutId = 0;
    let landingScroll: HTMLElement | null = null;
    const maxBindAttempts = 60;

    const pickActiveScroller = (): HTMLElement | null => {
      const wrap = tableWrapRef.current;
      const candidates = resolveAssetLogsInfiniteScrollTargets(wrap);
      scrollElementsRef.current = candidates;

      for (const element of candidates) {
        if (
          shouldLoadMoreAssetLogsTable(element, loadMoreScrollRatioRef.current)
        ) {
          return element;
        }
      }

      return candidates[0] ?? null;
    };

    const requestLoadMore = () => {
      if (
        loadMoreQueuedRef.current ||
        !enabledRef.current ||
        !hasNextPageRef.current ||
        !userHasScrolledTableRef.current ||
        isFetchingNextPageRef.current ||
        isFetchNextPageErrorRef.current
      ) {
        return;
      }

      loadMoreQueuedRef.current = true;
      void Promise.resolve(onLoadMoreRef.current()).finally(() => {
        loadMoreQueuedRef.current = false;
      });
    };

    const evaluateScroll = () => {
      if (!userHasScrolledTableRef.current) {
        return;
      }

      const scroller = pickActiveScroller();
      if (!scroller) {
        return;
      }

      if (
        !shouldLoadMoreAssetLogsTable(scroller, loadMoreScrollRatioRef.current)
      ) {
        return;
      }

      requestLoadMore();
    };

    evaluateScrollRef.current = evaluateScroll;

    const markUserScroll = () => {
      userHasScrolledTableRef.current = true;
    };

    const onScroll = () => {
      markUserScroll();
      evaluateScroll();
    };

    const onWheel = () => {
      markUserScroll();
      evaluateScroll();
    };

    const onTouchStart = () => {
      markUserScroll();
    };

    const bindScrollElements = () => {
      const wrap = tableWrapRef.current;
      if (!wrap) {
        scrollElementsRef.current = [];
        return false;
      }

      const candidates = resolveAssetLogsInfiniteScrollTargets(wrap);
      if (candidates.length === 0) {
        scrollElementsRef.current = [];
        return false;
      }

      scrollElementsRef.current = candidates;
      landingScroll =
        candidates.find((element) =>
          element.classList.contains('landing-content-scroll'),
        ) ?? resolveLandingContentScroll(wrap);

      for (const element of scrollElementsRef.current) {
        element.addEventListener('scroll', onScroll, { passive: true });
        element.addEventListener('wheel', onWheel, { passive: true });
        element.addEventListener('touchstart', onTouchStart, { passive: true });
      }

      wrap.addEventListener('wheel', onWheel, { passive: true });

      return true;
    };

    const unbindScrollElements = () => {
      const wrap = tableWrapRef.current;
      for (const element of scrollElementsRef.current) {
        element.removeEventListener('scroll', onScroll);
        element.removeEventListener('wheel', onWheel);
        element.removeEventListener('touchstart', onTouchStart);
      }
      scrollElementsRef.current = [];
      wrap?.removeEventListener('wheel', onWheel);
    };

    const tryBind = () => {
      if (bindScrollElements()) {
        return;
      }
      bindAttempts += 1;
      if (bindAttempts >= maxBindAttempts) {
        return;
      }
      rafId = requestAnimationFrame(() => {
        if (bindScrollElements()) {
          return;
        }
        timeoutId = window.setTimeout(tryBind, 50);
      });
    };

    tryBind();

    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      unbindScrollElements();
      landingScroll?.removeEventListener('scroll', onScroll);
      landingScroll?.removeEventListener('wheel', onWheel);
    };
  }, [enabled, tableWrapRef, loadMoreScrollRatio]);

  useEffect(() => {
    if (
      !enabled ||
      isFetchingNextPage ||
      isFetchNextPageError ||
      !userHasScrolledTableRef.current
    ) {
      return;
    }

    return scheduleAfterLayout(() => {
      evaluateScrollRef.current();
    });
  }, [enabled, isFetchingNextPage, isFetchNextPageError, loadedItemCount]);

  return {};
}
